import React, { type JSX } from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import Page404 from './Page404';
import { apiService } from '@services/ApiService';
import SnapshotEditor from '@components/SnapshotEditor';
import { ImgClock } from '@components/Images/ImgClock';

type HistorySnapshotDetail = {
  id: string;
  name: string;
  content: string;
  createdAt: Date;
  createdByUser: boolean;
};

type SnapshotNoteClassProps = {
  noteId?: string;
  snapshotId?: string;
};

@observer
class SnapshotNoteBase extends React.Component<SnapshotNoteClassProps> {
  @observable private _snapshotData: HistorySnapshotDetail | null = null;
  @observable private _isLoading = true;

  constructor(props: SnapshotNoteClassProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount(): void {
    this.loadSnapshot();
  }

  private loadSnapshot = async (): Promise<void> => {
    const noteId = this.props.noteId;
    const snapshotId = this.props.snapshotId;
    
    if (!noteId || !snapshotId) return;
    
    try {
      const data = await apiService.get<HistorySnapshotDetail>(`/api/note/${noteId}/history/${snapshotId}`);
      runInAction(() => {
        this._snapshotData = data;
        
        // Update document title with snapshot name
        document.title = 'Snapshot - ' + (data.name || 'Untitled');
      });
    } catch (error) {
      console.error('Failed to load snapshot:', error);
    } finally {
      runInAction(() => {
        this._isLoading = false;
      });
    }
  };

  render(): JSX.Element {
    if (this._isLoading) {
      return <div className="snapshot-note-wrapper"><div className="modal-loading">Loading snapshot...</div></div>;
    }

    if (!this._snapshotData) {
      return <Page404 />;
    }

    return (
      <>
        <div className="snapshot-note-wrapper">
          {/* Snapshot header: title + read-only metadata */}
          <div className="snapshot-info-banner">
            <div className="snapshot-info-title">
              <span className="snapshot-info-icon"><ImgClock size={18} /></span>
              <h3>{this._snapshotData.name || '(Untitled)'}</h3>
            </div>
            <div className="snapshot-info-meta">
              <span className={`snapshot-info-badge${this._snapshotData.createdByUser ? '' : ' auto'}`}>
                {this._snapshotData.createdByUser ? 'Manual' : 'Automatic'}
              </span>
              <span className="snapshot-info-date">
                {new Date(this._snapshotData.createdAt).toLocaleString()}
              </span>
              <span className="snapshot-info-lock">Read-only snapshot</span>
            </div>
          </div>

          <SnapshotEditor
            name={this._snapshotData.name}
            content={this._snapshotData.content}
            createdAt={this._snapshotData.createdAt}
            createdByUser={this._snapshotData.createdByUser}
          />
        </div>
      </>
    );
  }
}

// Functional wrapper to access route params via useParams hook
function SnapshotNoteWrapper(): JSX.Element {
  const { noteId, snapshotId } = useParams<{ noteId?: string; snapshotId?: string }>();
  
  return <SnapshotNoteBase noteId={noteId} snapshotId={snapshotId} />;
}

export default SnapshotNoteWrapper;